import { Role, OfficeType } from '@caresend/types';
import { getStore } from './helpers/initStore.js';
import { ConsumingApp } from './modules/app/model.js';

// Using vuex private properties in this file.
/* eslint-disable no-underscore-dangle */
/** Checks for existence of optional mutation */
const hasMutation = (store, mutationName) => { var _a; return ((_a = store === null || store === void 0 ? void 0 : store._mutations) === null || _a === void 0 ? void 0 : _a[mutationName]) !== undefined; };
/**
 * Call commits for optional mutations. Use only after confirming the mutation’s
 * existence with `hasMutation`.
 */
const customCommit = (commit, mutationName, payload) => {
    commit(mutationName, payload);
};
/** Checks for existence of optional action */
const hasAction = (store, actionName) => { var _a; return ((_a = store._actions) === null || _a === void 0 ? void 0 : _a[actionName]) !== undefined; };
/**
 * Confirms an optional action’s existence and dispatches it if it exists,
 * and does nothing otherwise.
 *
 * Pass action(s) object as type parameter.
 *
 * TODO: Improve type safety. Currently accepts any payload type that's listed
 * in the actions object. Should narrow to the specific payload type for the
 * action being dispatched. Action return types should be inferred.
 */
const optionalDispatch = (store, actionName, ...args) => {
    if (hasAction(store, actionName)) {
        return store.dispatch(actionName, ...args);
    }
};
/**
 * Confirms an optional action’s existence and dispatches it if it exists,
 * and throws an error otherwise.
 *
 * Pass action(s) object as type parameter.
 *
 * TODO: Improve type safety. Currently accepts any payload type that's listed
 * in the actions object. Should narrow to the specific payload type for the
 * action being dispatched. Action return types should be inferred.
 */
const dispatchOrThrow = (store, actionName, ...args) => {
    if (hasAction(store, actionName)) {
        return store.dispatch(actionName, ...args);
    }
    throw Error(`Missing action ${actionName}.`);
};
/**
 * Get the current user’s role.
 *
 * We do not currently support accessing `rootGetters` on actions in store
 * modules definted in ui-components, so we can use this function rather than
 * `auth/getUserRole`.
 */
const getUserRole = () => {
    var _a, _b;
    const store = getStore();
    return (_b = (_a = store.state.auth) === null || _a === void 0 ? void 0 : _a.user) === null || _b === void 0 ? void 0 : _b.role;
};
/**
 * Based on logged in user role, determine if the user is a dashboard user. If
 * the user is logged out, will return false regardless of which app is in use.
 */
const isDashboardUser = () => {
    const role = getUserRole();
    if (!role)
        return false;
    return [
        Role.RECRUITER,
        Role.SUPERADMIN,
    ].includes(role);
};
/**
 * Based on logged in user role, determine if the user is a pro user. If the
 * user is logged out, will return false regardless of which app is in use.
 */
const isProUser = () => {
    const role = getUserRole();
    if (!role)
        return false;
    return [
        Role.ASSISTANT,
        Role.NURSE,
        Role.PACKER,
        Role.PRESCRIBER,
    ].includes(role);
};
/**
 * Based on logged in user role, determine if the user is a patient app user.
 * If the user is logged out, will return false regardless of which app is in
 * use.
 */
const isPatientUser = () => {
    const role = getUserRole();
    return role === Role.PATIENT;
};
const isLabOfficeUser = () => {
    var _a, _b;
    const store = getStore();
    return ((_b = (_a = store.state.office) === null || _a === void 0 ? void 0 : _a.office) === null || _b === void 0 ? void 0 : _b.type) === OfficeType.LAB;
};
const getConsumingAppForCurrentUser = () => {
    if (isDashboardUser())
        return ConsumingApp.DASHBOARD;
    if (isPatientUser())
        return ConsumingApp.PATIENT;
    // Pro is default
    return ConsumingApp.PRO;
};
const setConsumingAppForCurrentUser = () => {
    const app = getConsumingAppForCurrentUser();
    const store = getStore();
    store.commit('app/SET_APP', app);
};

export { customCommit, dispatchOrThrow, getConsumingAppForCurrentUser, getUserRole, hasAction, hasMutation, isDashboardUser, isLabOfficeUser, isPatientUser, isProUser, optionalDispatch, setConsumingAppForCurrentUser };
